import React, { lazy, memo, Suspense } from 'react';

import { Box, Flex, Text } from '@opendoor/bricks/core';

import { WelcomeToHubCarousel } from 'growth/components/welcomeToHub/WelcomeToHubCarousel';
import { useWelcomeToHubContext } from 'growth/components/welcomeToHub/WelcomeToHubContext';
import LoadingDotsLottie from 'growth/lottie/LoadingDotsLottieFile.json';

export interface IWelcomeToHubLoadingPageData {
  timedMessageIndex: number;
}

export const timedMessages = [
  {
    xs: 'This will be fast. Most offers appear in seconds.',
    aboveXs: 'This will be fast. Most offers appear in seconds.',
  },
  {
    xs: "Almost there. There's a lot that goes into every offer. We'll have yours ready in a moment.",
    aboveXs: (
      <React.Fragment>
        Almost there. There's a lot that goes into every <br />
        offer. We'll have yours ready in a moment.
      </React.Fragment>
    ),
  },
  {
    xs: "It's taking slightly longer than normal to prepare an offer for you. Hang tight, it'll be ready shortly.",
    aboveXs: (
      <React.Fragment>
        It's taking slightly longer than normal to prepare <br />
        an offer for you. Hang tight, it'll be ready shortly.
      </React.Fragment>
    ),
  },
];

// Lazy loading to save 40kb on main bundle
const LottieSvgLoader = lazy(
  () =>
    import(
      /* webpackChunkName: "LottieSvgLoader" */
      '../LottieSvgLoader'
    ),
);

const LoadingDotsAnimation = () => {
  return (
    <Suspense fallback={<Box />}>
      <LottieSvgLoader
        animationData={LoadingDotsLottie}
        height="12px"
        loop={true}
        name="loading-dots"
        width="27px"
      />
    </Suspense>
  );
};

const MemoLoadingDotsAnimation = memo(LoadingDotsAnimation);

export type WelcomeToHubVariant = 'offer_refresh_from_email' | 'welcome_to_the_hub_v3' | null;

/**
 * Alternate loading page with gradient background
 *
 * storybook-check-ignore
 *  the page has a loading indicator that will make the storybook flaky
 */
export const WelcomeToHubLoadingPage: React.FC<{ variant: WelcomeToHubVariant }> = ({
  variant,
}) => {
  const { welcomeToHubLoadingPageData } = useWelcomeToHubContext();
  let loadingTitle = 'Preparing your offer';
  if (variant === 'offer_refresh_from_email') {
    loadingTitle = 'Refreshing your offer';
  }

  return (
    <Box>
      <Flex
        css={`
          background-image: linear-gradient(120.36deg, #fff6f4 -1.19%, #e7effe 73.53%);
        `}
        minHeight="100vh"
        width="100vw"
        flexDirection="column"
        alignItems="center"
        textAlign="center"
        pb={[0, 8]}
        pt={7}
      >
        <Flex alignItems="center" justifyContent="center">
          <MemoLoadingDotsAnimation />
        </Flex>
        <Text fontSize="s3" fontWeight="bold" pt={5}>
          {loadingTitle}
        </Text>
        {/* Display text with br for screen widths above xs */}
        <Text fontSize="s0" width={['100%', '324px', '100%']} display={['block', 'block', 'none']}>
          {timedMessages[welcomeToHubLoadingPageData.timedMessageIndex].xs}
        </Text>
        <Text fontSize="s0" display={['none', 'none', 'block']}>
          {timedMessages[welcomeToHubLoadingPageData.timedMessageIndex].aboveXs}
        </Text>
        <WelcomeToHubCarousel />
      </Flex>
    </Box>
  );
};
